"use client";

import { useSystemDiagnostics } from "@/components/hooks/use-system-diagnostics";
import { useSystemStore } from "@/lib/store";
import { useT } from "@/lib/i18n";
import { SectionLabel } from "@/components/ui/section-label";
import { BatteriesSection } from "./batteries-section";
import { formatHours, formatW, splitValueUnit } from "./format";

export function SimulatePanel() {
  const tr = useT();
  const system = useSystemStore((s) => s.system);
  const componentStates = useSystemStore((s) => s.simulateState.componentStates);
  const batterySoc = useSystemStore((s) => s.simulateState.batterySoc);
  const loadStates = useSystemStore((s) => s.simulateState.loadStates);

  const { budget } = useSystemDiagnostics(
    system, componentStates, batterySoc, undefined, loadStates,
  );

  const componentCount = system.nodes.filter((n) => n.kind === "component").length;
  const isEmpty = componentCount === 0;

  if (isEmpty) {
    return (
      <div className="px-5 py-10 text-center">
        <div className="text-[13px] font-medium tracking-tight">
          Nothing to simulate yet
        </div>
        <div className="text-[11.5px] text-[var(--color-fg-muted)] mt-1.5 leading-relaxed">
          Add components and wire them up — Simulate will track autonomy and
          flow as your system grows.
        </div>
      </div>
    );
  }

  const netPositive = budget.netInstantW >= 0;
  const netParts = splitValueUnit(formatW(Math.abs(budget.netInstantW)));

  return (
    <div className="flex flex-col">
      <div className="p-5">
        <SectionLabel className="mb-2">{tr("health.net_now")}</SectionLabel>
        <div className="flex items-baseline gap-1.5">
          <span
            className="text-[44px] font-medium leading-none tabular-nums"
            style={{
              color: netPositive
                ? "var(--color-success)"
                : "var(--color-positive)",
              fontFeatureSettings: '"ss01", "tnum"',
            }}
          >
            {netPositive ? "+" : "−"}
            {netParts.value}
          </span>
          <span className="text-[22px] text-[var(--color-fg-muted)]">
            {netParts.unit}
          </span>
        </div>
        <div className="mt-5 space-y-1.5">
          {[
            {
              label: tr("health.time_to_empty"),
              value: formatHours(budget.timeToEmptyHours),
              hint: tr("health.time_to_empty_hint"),
            },
            {
              label: tr("health.time_to_full"),
              value: formatHours(budget.timeToFullHours),
              hint: tr("health.time_to_full_hint"),
            },
          ].map(({ label, value, hint }) => {
            const isInactive = value === "∞";
            const parts = splitValueUnit(value);
            return (
              <div
                key={label}
                title={hint}
                className="flex items-baseline gap-3"
              >
                <span className="flex-1 min-w-0 text-[11.5px] text-[var(--color-fg-muted)] truncate">
                  {label}
                </span>
                {isInactive ? (
                  <span className="flex-none font-mono tabular-nums text-[12.5px] text-[var(--color-fg-subtle)]">
                    —
                  </span>
                ) : (
                  <span className="flex-none inline-flex items-baseline gap-0.5 font-mono tabular-nums">
                    <span className="text-[13px] font-medium leading-none">
                      {parts.value}
                    </span>
                    {parts.unit && (
                      <span className="text-[10px] text-[var(--color-fg-subtle)] tracking-wide">
                        {parts.unit}
                      </span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <BatteriesSection />
    </div>
  );
}
